import { Heading, Tooltip } from '@mm/components';
import CompanyModel from '@mm/state/model/CompanyModel';
import { StoreState } from '@mm/state/store-interfaces';
import bestChatbotPlatform from 'assets/best-chatbot-platform.svg';
import bestWebPlatform from 'assets/best-web-platform.svg';
import userInfoNeil from 'assets/neil-patel-transparent.svg';
import { Formik, FormikProps } from 'formik';
import React from 'react';
import { useSelector } from 'react-redux';
import FormFieldWrapper from '../../../../../../../common/components/FormFieldWrapper/FormFieldWrapper';
import { PhoneInput } from '../../../../../../../common/components/PhoneInput/PhoneInput';
import { WrappedTextInput } from '../../../../../../../common/components/WrappedTextInput/WrappedTextInput';
import { getDeviceInfo } from '../../../../../../../common/hooks/use-device-info';
import { useResolutionInfo } from '@mm/hooks';
import CompleteRegisterButton from '../../../2-UserInfo/CompleteRegisterButton/CompleteRegisterButton';
import {
  fields,
  getMainFiledValidationSchema,
  UserInfoFormValues,
} from '../../../2-UserInfo/user-info-form-helpers';
import './WelcomePage.scss';
import { SignUpLogo } from '../../../../../SignUp/SignUp/SignUpLogo/SignUpLogo';
import { companyFeatureFlagsSelector } from '@mm/state/selectors/featureFlagsSelectors';
import { selectCurrentUser } from '@mm/state/selectors/userSelector';

type Props = {
  onStartPress: (
    companyName: string,
    businessEmail: string,
    companyWebsite: string,
    phoneNumber: string,
  ) => void;
  company: CompanyModel;
};

export const WelcomePage = (props: Props) => {
  const { onStartPress, company } = props;
  const { isMobile } = useResolutionInfo();
  const { isAndroid, isIOS } = getDeviceInfo();
  const userCountry = useSelector((store: StoreState) => store.env.userCountry);
  const isNewSignupFlow = useSelector(
    companyFeatureFlagsSelector,
  ).onboarding2022;
  const user = useSelector(selectCurrentUser);

  const isBusinessEmail = () => {
    const stopList = [
      'gmail.com',
      'yahoo.com',
      'hotmail.com',
      'aol.com',
      'outlook.com',
      'icloud.com',
      'mac.com',
      'msn.com',
      'sbcglobal.net',
      'yahoo.co.uk',
    ];
    const res = stopList.filter(v => user?.email.includes(v));
    return !res.length;
  };

  const validationSchema = getMainFiledValidationSchema();
  const initialValues = {
    businessEmail:
      isNewSignupFlow && isBusinessEmail()
        ? user?.email
        : company.businessEmail ?? '',
    companyName: isNewSignupFlow ? '' : company.name,
    companyWebsite: company.website ?? '',
    phoneNumber: company.phoneNumber ?? '',
  };

  const onSignupClick = async (formValues: UserInfoFormValues) => {
    const { businessEmail, companyName, companyWebsite, phoneNumber } =
      formValues;

    onStartPress(companyName, businessEmail, companyWebsite, phoneNumber);
  };

  const getIsValid = (formProps: FormikProps<UserInfoFormValues>) => {
    if (isMobile) {
      return false;
    }
    return !formProps.isValid;
  };

  return (
    <div className="welcome_page_wrapper">
      <div className="welcome_page_background">
        <div className="welcome-page__left-section">
          <SignUpLogo className="welcome-page__logo" />
          <img
            src={bestWebPlatform}
            className="welcome-page__best-web-platform-img"
          />
          <img
            src={bestChatbotPlatform}
            className="welcome-page__best-chatbot-platform-img"
          />
        </div>

        <div className="welcome-page__right-section">
          <div className="welcome-page__right-section-content">
            <img src={userInfoNeil} className="welcome-page__neil-img" />
            <div className="welcome-page__neil-img-label">Neil Patel</div>
            <div className="welcome-page__neil-img-label">
              Forbes Top 10 Marketer
            </div>
            <div className="welcome-page__description">
              <div>
                For some reason, not very many marketers are taking advantage of
                Messanger marketing, even though it’s the hottest opportunity in
                digital marketing today.
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="welcome-page__content">
        <Heading
          className="welcome-page__content-title"
          size={1}
          text="Welcome to MobileMonkey"
        />
        <Heading
          className="welcome-page__content-subtitle"
          size={1}
          text="Please tell us more about your business"
        />
        <div className="welcome-page__form-container">
          <Formik
            onSubmit={onSignupClick}
            initialValues={initialValues}
            validationSchema={validationSchema}
            // Until the below issue is fixed on mobile only validate on submit
            // due to autocomplete issues with certain combos of Android/Chrome
            // https://github.com/facebook/react/issues/1159
            validateOnChange={!isMobile}
            validateOnBlur={!isMobile}
            enableReinitialize
          >
            {(formProps: FormikProps<UserInfoFormValues>) => (
              <form onSubmit={() => onSignupClick(formProps.values)}>
                <div className="welcome-page__form-wrapper">
                  <WrappedTextInput
                    label="Company Name:"
                    inputClassName="welcome-page__form-input"
                    formProps={formProps}
                    fieldName={fields.companyName}
                  />

                  {(isNewSignupFlow ? !isBusinessEmail() : true) && (
                    <WrappedTextInput
                      label="Business Email:"
                      inputClassName="welcome-page__form-input"
                      formProps={formProps}
                      fieldName={fields.businessEmail}
                    />
                  )}
                  <Tooltip
                    position="right"
                    color="light"
                    useHover
                    noDelayClose
                    anchorElement={
                      <WrappedTextInput
                        label="Website URL:"
                        inputClassName="welcome-page__form-input"
                        formProps={formProps}
                        fieldName={fields.companyWebsite}
                      />
                    }
                  >
                    Please, list your website’s URL that you would like to
                    collect its visitor information and generate traffic to
                  </Tooltip>

                  <FormFieldWrapper
                    className="user-info__form-wrapper"
                    label="Phone Number"
                    error={formProps.errors.phoneNumber}
                    touched={formProps.touched.phoneNumber}
                  >
                    <PhoneInput
                      country={userCountry}
                      name={fields.phoneNumber}
                      className="user-info__phone-input"
                      value={formProps.values.phoneNumber}
                      onChange={value =>
                        formProps.setFieldValue('phoneNumber', value)
                      }
                      onBlur={() => formProps.setFieldTouched('phoneNumber')}
                      errorState={
                        formProps.touched.phoneNumber &&
                        !!formProps.errors.phoneNumber
                      }
                    />
                  </FormFieldWrapper>
                  <input
                    className="user-info__submit-input"
                    type="submit"
                    disabled={getIsValid(formProps)}
                  />
                  <CompleteRegisterButton
                    useNewDesign
                    disabled={getIsValid(formProps)}
                    isMobileApp={isAndroid || isIOS}
                    onSentToMessenger={formProps.handleSubmit}
                  />
                </div>
              </form>
            )}
          </Formik>
        </div>
      </div>
    </div>
  );
};
